// src/pages/Classes.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Box, Typography, Paper, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Stack,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, CircularProgress, Snackbar, Alert, Chip,
    InputAdornment, Tooltip, Avatar,
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
    Refresh as RefreshIcon, Search as SearchIcon,
    School as SchoolIcon,
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { getClasses, createClass, updateClass, deleteClass, ClassUpsertDto } from '../services/subjectService';
import { TableShimmer } from '../components/Shimmer';

const PRIMARY = '#174143';
const ACCENT = '#3d9ea4';

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(16px); }
  to   { opacity:1; transform:translateY(0); }
`;

// Generates a deterministic soft colour for each class row avatar
const CLASS_GRADIENTS = [
    'linear-gradient(135deg,#667eea,#764ba2)',
    'linear-gradient(135deg,#4facfe,#00f2fe)',
    'linear-gradient(135deg,#43e97b,#38f9d7)',
    'linear-gradient(135deg,#f093fb,#f5576c)',
    'linear-gradient(135deg,#fa709a,#fee140)',
    'linear-gradient(135deg,#a18cd1,#fbc2eb)',
    'linear-gradient(135deg,#ffecd2,#fcb69f)',
    'linear-gradient(135deg,#ff9a9e,#fad0c4)',
    'linear-gradient(135deg,#30cfd0,#330867)',
    'linear-gradient(135deg,#96fbc4,#f9f586)',
];

export default function ClassesPage(): React.ReactElement {
    const [classes, setClasses] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [search, setSearch] = useState('');

    // dialog
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState<ClassUpsertDto>({ name: '' });
    const [nameError, setNameError] = useState('');

    // ── Data ──────────────────────────────────────────────
    const fetchClasses = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const data = await getClasses(0, 100);
            setClasses(Array.isArray(data) ? data : (data?.content ?? []));
        } catch (err: any) {
            setError(err.message || 'Failed to load classes');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchClasses(); }, [fetchClasses]);

    // ── Filtered ───────────────────────────────────────────
    const filtered = useMemo(() => {
        if (!search.trim()) return classes;
        return classes.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
    }, [classes, search]);

    // ── Handlers ──────────────────────────────────────────
    const handleOpenCreate = () => {
        setEditingId(null); setForm({ name: '' }); setNameError(''); setOpenDialog(true);
    };
    const handleOpenEdit = (cls: any) => {
        setEditingId(cls.id); setForm({ name: cls.name }); setNameError(''); setOpenDialog(true);
    };
    const handleDelete = async (id: number) => {
        if (!window.confirm('Delete this class? This may affect linked subjects.')) return;
        try {
            await deleteClass(id);
            setSnackbar({ type: 'success', msg: 'Class deleted' });
            fetchClasses();
        } catch (err: any) {
            setSnackbar({ type: 'error', msg: err.message || 'Delete failed' });
        }
    };
    const handleSubmit = async () => {
        if (!form.name.trim()) { setNameError('Class name is required'); return; }
        setSaving(true);
        try {
            if (editingId) {
                await updateClass(editingId, form);
                setSnackbar({ type: 'success', msg: 'Class updated' });
            } else {
                await createClass(form);
                setSnackbar({ type: 'success', msg: 'Class created' });
            }
            setOpenDialog(false);
            fetchClasses();
        } catch (err: any) {
            setSnackbar({ type: 'error', msg: err.message || 'Operation failed' });
        } finally { setSaving(false); }
    };

    // ── Render ────────────────────────────────────────────
    return (
        <Box sx={{ minHeight: '100vh', background: '#f5f6fa', p: { xs: 2, md: 3 } }}>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>

                {/* ── Hero Header ── */}
                <Paper
                    elevation={0}
                    sx={{
                        mb: 3, borderRadius: 4, overflow: 'hidden',
                        background: `linear-gradient(135deg, ${PRIMARY} 0%, #2a6b6f 60%, ${ACCENT} 100%)`,
                        color: '#fff',
                        animation: `${fadeUp} 0.4s ease-out`,
                        position: 'relative',
                    }}
                >
                    <Box sx={{ position: 'absolute', top: -50, right: -30, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
                    <Box sx={{ position: 'absolute', bottom: -20, right: 120, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />

                    <Box sx={{ p: { xs: 3, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2.5, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <SchoolIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.5 }}>Class Management</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
                                    Create and manage school classes used across subjects
                                </Typography>
                            </Box>
                        </Box>

                        <Chip
                            label={`${classes.length} ${classes.length === 1 ? 'Class' : 'Classes'}`}
                            sx={{ background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', px: 1 }}
                        />
                    </Box>
                </Paper>

                {/* ── Toolbar ── */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', p: 2, mb: 2.5, animation: `${fadeUp} 0.4s ease-out 60ms both` }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="Search classes…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: 'text.disabled' }} /></InputAdornment> }}
                            sx={{ flex: 1, minWidth: 200 }}
                        />
                        <Box sx={{ flex: 1 }} />
                        <Tooltip title="Refresh">
                            <IconButton onClick={fetchClasses} sx={{ border: '1px solid', borderColor: 'divider' }}>
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, boxShadow: '0 4px 14px rgba(23,65,67,0.3)', fontWeight: 700, px: 2.5, borderRadius: 2 }}
                        >
                            New Class
                        </Button>
                    </Box>

                    {search && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                Showing {filtered.length} of {classes.length} classes
                            </Typography>
                        </Box>
                    )}
                </Paper>

                {/* ── Table ── */}
                {loading ? (
                    <TableShimmer rows={6} cols={4} />
                ) : error ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography color="error">⚠ {error}</Typography>
                    </Box>
                ) : (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden', animation: `${fadeUp} 0.4s ease-out 120ms both` }}>
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        {['#', 'Class Name', 'Actions'].map((h, i) => (
                                            <TableCell
                                                key={i}
                                                align={i === 2 ? 'right' : 'left'}
                                                sx={{ fontWeight: 700, fontSize: '0.72rem', letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary', background: '#f8fafc', py: 1.5 }}
                                            >
                                                {h}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filtered.map((cls, idx) => (
                                        <TableRow
                                            key={cls.id}
                                            hover
                                            sx={{ '&:hover': { background: '#f0fafa' }, transition: 'background 0.15s' }}
                                        >
                                            <TableCell sx={{ width: 50, color: 'text.disabled', fontSize: '0.75rem' }}>
                                                {idx + 1}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 36, height: 36,
                                                            background: CLASS_GRADIENTS[idx % CLASS_GRADIENTS.length],
                                                            fontSize: '0.8rem', fontWeight: 800,
                                                        }}
                                                    >
                                                        {cls.name.replace(/\D/g, '') || cls.name[0]?.toUpperCase()}
                                                    </Avatar>
                                                    <Typography variant="body2" fontWeight={700} color={PRIMARY}>
                                                        {cls.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <Tooltip title="Edit">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenEdit(cls)}
                                                            sx={{ color: PRIMARY, '&:hover': { background: 'rgba(23,65,67,0.08)' } }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(cls.id)}
                                                            sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239,68,68,0.08)' } }}
                                                        >
                                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {filtered.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 8, color: 'text.disabled' }}>
                                                <SchoolIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: 'block', mx: 'auto' }} />
                                                {search ? 'No classes match your search.' : 'No classes found. Create your first one!'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                )}
            </Box>

            {/* ── Dialog ── */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                {/* Gradient header */}
                <Box sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, px: 3, py: 2.5, color: '#fff' }}>
                    <Typography variant="h6" fontWeight={800}>{editingId ? 'Edit Class' : 'New Class'}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.75 }}>
                        {editingId ? 'Update the class name below.' : 'Enter a name for the new class.'}
                    </Typography>
                </Box>

                <DialogContent dividers sx={{ pt: 2.5, pb: 2 }}>
                    <TextField
                        label="Class Name *"
                        fullWidth
                        size="small"
                        value={form.name}
                        onChange={e => { setForm({ name: e.target.value }); setNameError(''); }}
                        placeholder="e.g. Class 10, Grade 8…"
                        error={!!nameError}
                        helperText={nameError}
                        autoFocus
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SchoolIcon fontSize="small" sx={{ color: ACCENT }} />
                                </InputAdornment>
                            ),
                        }}
                        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                    />
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={saving}
                        sx={{ background: `linear-gradient(135deg, ${PRIMARY}, ${ACCENT})`, fontWeight: 700, px: 3, borderRadius: 2 }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : (editingId ? 'Update Class' : 'Create Class')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnackbar(null)} severity={snackbar?.type ?? 'info'} variant="filled" sx={{ borderRadius: 2 }}>
                    {snackbar?.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
